// Service definitions and city-specific content for hyper-local SEO pages

export const services = {
  'shower-glass': {
    name: 'Shower Glass',
    shortName: 'Shower Glass',
    parentPath: '/shower-glass',
    heroImage: '/images/services/shower-glass-hero.jpeg',
    description: 'Transform your bathroom with custom frameless and semi-frameless shower enclosures. Our precision craftsmanship delivers ultra-tight tolerances using laser templating technology for a perfect fit every time.',
    features: [
      'Frameless & semi-frameless enclosure designs',
      'Low-iron ultra-clear glass eliminates green tint',
      'Ultra-tight gap tolerances via laser templating',
      'ShowerGuard® ion-beam protective coating',
      'Premium hardware: matte black, brushed nickel, polished chrome, oil-rubbed bronze, satin brass',
      'Swing door, sliding barn door, and pivot hinge configurations',
      'CR Laurence and FHC heavy-duty hardware systems',
      'Free in-home consultation with 3D design preview',
    ],
    technicalSpecs: [
      { label: 'Glass Thickness', value: '3/8" (10mm) standard or 1/2" (12mm) premium tempered safety glass' },
      { label: 'Glass Types', value: 'Standard clear, low-iron Starphire® ultra-clear, acid-etched frosted, rain texture, fluted/reeded, and ShowerGuard® coated' },
      { label: 'Hardware Finishes', value: 'Polished chrome, brushed nickel, matte black, oil-rubbed bronze, satin brass, polished brass, brushed gold' },
      { label: 'Enclosure Configurations', value: 'Inline/single door, 90-degree corner, neo-angle, sliding bypass, barn door slider, steam shower rated' },
      { label: 'Hinge Types', value: 'Wall-mount, glass-to-glass, pivot, continuous piano hinge for heavy doors' },
      { label: 'Tolerance Standard', value: 'Ultra-tight precision gaps achieved through digital laser templating' },
    ],
    detailedContent: {
      glassTypes: [
        { name: 'Standard Clear Tempered', description: 'Classic tempered safety glass with slight green tint from iron content. Most economical option.' },
        { name: 'Low-Iron Starphire® Ultra-Clear', description: 'Premium glass with iron removed for crystal-clear transparency. No green tint. Ideal for white bathrooms.' },
        { name: 'ShowerGuard® Coated Glass', description: 'Permanent ion-beam coating fused during manufacturing. Resists hard water stains, soap scum, and corrosion. Lifetime warranty.' },
        { name: 'Acid-Etched Frosted', description: 'Factory-applied frosted finish for privacy. Smoother than sandblasted, easier to clean.' },
        { name: 'Rain Glass', description: 'Textured pattern provides privacy while allowing light transmission. Classic European aesthetic.' },
      ],
      hardwareOptions: [
        { name: 'Matte Black', description: 'Modern industrial aesthetic. Powder-coated for durability. Pairs with contemporary fixtures.' },
        { name: 'Brushed Nickel', description: 'Warm silver tone with satin finish. Hides fingerprints and water spots. Most popular choice.' },
        { name: 'Polished Chrome', description: 'Mirror-like reflective finish. Classic and timeless. Matches traditional bathrooms.' },
        { name: 'Oil-Rubbed Bronze', description: 'Dark bronze with copper highlights. Rustic elegance for transitional designs.' },
        { name: 'Satin Brass/Brushed Gold', description: 'Warm gold tones. On-trend for luxury and modern farmhouse designs.' },
      ],
      doorTypes: [
        { name: 'Swing Door (Hinged)', description: 'Traditional outward-opening door. Requires clearance. Best for larger bathrooms.' },
        { name: 'Pivot Hinge Door', description: 'Door pivots from top and bottom points. Sleek frameless look. Self-centering.' },
        { name: 'Sliding Bypass', description: 'Two panels slide past each other. Space-saving for tub/shower combos.' },
        { name: 'Barn Door Slider', description: 'Single panel slides on exposed track. Modern statement piece. Popular in contemporary designs.' },
      ],
    },
    ctaText: 'Get a Free Shower Glass Quote',
    ctaSubtext: 'Laser-templated and professionally installed by our expert team.',
  },
  'wine-enclosures': {
    name: 'Wine Enclosures',
    shortName: 'Wine Enclosures',
    parentPath: '/wine-enclosures',
    heroImage: '/images/services/wine_room.jpg',
    description: 'Elegant glass wine cellars engineered for serious collectors. Our thermally-broken aluminum framing and climate-sealed glass systems maintain optimal 55°F storage temperature and 60-70% humidity levels.',
    features: [
      'Thermally-broken aluminum frame systems',
      'Dual-pane insulated glass units (IGUs)',
      'Low-E coated glass blocks 99% UV rays',
      'Magnetic or compression door seals',
      'Integrated LED display lighting options',
      'Cooling system compatibility (WhisperKOOL, CellarPro, Wine Guardian)',
      'Humidity-resistant hardware finishes',
      'Custom sizes from wine closets to full cellars',
    ],
    technicalSpecs: [
      { label: 'Glass Options', value: '1/2" tempered single-pane or dual-pane insulated glass units (IGUs) with argon fill' },
      { label: 'Thermal Break', value: 'Aluminum framing with polyamide thermal break prevents condensation' },
      { label: 'UV Protection', value: 'Low-E coating blocks 99% UV radiation to prevent wine degradation' },
      { label: 'Seal Rating', value: 'Magnetic gaskets and compression seals rated for 10°F+ differential' },
      { label: 'Temperature Range', value: 'Systems designed for 55°F cellar temps with ambient up to 85°F' },
      { label: 'Configurations', value: 'Full-room cellars, under-stair wine closets, walk-in caves, glass display walls' },
    ],
    detailedContent: {
      glassTypes: [
        { name: 'Single-Pane Tempered', description: 'Basic option for climate-controlled rooms. Requires powerful cooling system.' },
        { name: 'Dual-Pane IGU', description: 'Insulated glass unit with argon gas fill. Superior thermal performance. Recommended for most installations.' },
        { name: 'Triple-Pane IGU', description: 'Maximum insulation for extreme temperature differentials or passive cooling.' },
        { name: 'Low-E Coated', description: 'Metallic coating reflects heat and UV rays. Essential for protecting wine from light damage.' },
      ],
      coolingCompatibility: [
        { name: 'WhisperKOOL', description: 'Industry-leading quiet operation. Through-wall and ducted systems.' },
        { name: 'CellarPro', description: 'High-performance units for demanding applications. Self-contained and split systems.' },
        { name: 'Wine Guardian', description: 'Commercial-grade reliability. Ducted systems for large cellars.' },
      ],
    },
    ctaText: 'Get a Free Wine Enclosure Quote',
    ctaSubtext: 'Engineered for optimal temperature control and stunning visual display.',
  },
  'office-partitions': {
    name: 'Office Partitions',
    shortName: 'Office Glass',
    parentPath: '/office-partitions',
    heroImage: '/images/services/U-channel-Office-Enclosure.jpeg',
    description: 'Create open, collaborative workspaces with modern glass partition systems. Our solutions include acoustic-rated laminated glass achieving STC 35-42 ratings for speech privacy in conference rooms and executive offices.',
    features: [
      'Floor-to-ceiling glass wall systems',
      'Frameless all-glass and aluminum-framed options',
      'Acoustic laminated glass (STC 35-42 rating)',
      'Frosted, clear, switchable smart glass privacy options',
      'Integrated glass doors: swing, sliding, pivot',
      'Aluminum U-channel and point-fixed mounting',
      'Cable management and power integration',
      'Weekend/after-hours installation available',
    ],
    technicalSpecs: [
      { label: 'Glass Types', value: 'Clear tempered, frosted/acid-etched, laminated acoustic, switchable PDLC smart glass' },
      { label: 'Frame Systems', value: 'Aluminum U-channel (floor/ceiling), point-fixed spider fittings, frameless all-glass' },
      { label: 'Sound Ratings', value: 'STC 35 (standard), STC 38-42 (acoustic laminated with proper seals)' },
      { label: 'Glass Thickness', value: '3/8" tempered standard, 1/2" for larger panels, laminated for acoustics' },
      { label: 'Door Options', value: 'Frameless glass swing, sliding barn, pivot, and automatic sliding' },
      { label: 'Privacy Solutions', value: 'Acid-etched frosted, film applications, switchable electric smart glass' },
    ],
    detailedContent: {
      glassTypes: [
        { name: 'Clear Tempered', description: 'Maximum light transmission. Creates open, connected workspace feel.' },
        { name: 'Acid-Etched Frosted', description: 'Factory-applied matte finish. Provides privacy while maintaining light flow.' },
        { name: 'Acoustic Laminated', description: 'PVB interlayer between glass panes absorbs sound. Essential for conference rooms.' },
        { name: 'Switchable Smart Glass (PDLC)', description: 'Electrically switchable from clear to opaque. Instant privacy on demand.' },
      ],
      mountingSystems: [
        { name: 'Aluminum U-Channel', description: 'Glass sits in floor and ceiling channels. Clean lines. Most common system.' },
        { name: 'Point-Fixed/Spider', description: 'Glass attached at discrete points. Maximum transparency. Modern aesthetic.' },
        { name: 'Frameless All-Glass', description: 'Glass-to-glass connections. No visible framing. Ultimate minimalism.' },
      ],
    },
    ctaText: 'Get a Free Office Partition Quote',
    ctaSubtext: 'Minimal disruption installation for your business.',
  },
  'mirrors': {
    name: 'Mirrors',
    shortName: 'Mirrors',
    parentPath: '/mirrors',
    heroImage: '/images/mirrors/mirror.jpg',
    description: 'Custom-cut mirrors precision-fabricated for bathrooms, bedrooms, gyms, and commercial spaces. We offer 1/4" standard and 3/8" heavy-duty options with polished, beveled, or flat edge finishes.',
    features: [
      'Custom sizes up to 96" x 130" single piece',
      '1/4" standard or 3/8" heavy-duty thickness',
      'Polished, beveled (1" or 1.5"), and flat edges',
      'Safety backing (vinyl or film) options',
      'Anti-fog heating pad integration',
      'Backlit LED mirror systems',
      'J-bar, Z-clip, and adhesive mounting',
      'Gym mirror wall systems',
    ],
    technicalSpecs: [
      { label: 'Mirror Types', value: 'Silver-backed float glass, copper-free/moisture-resistant, antiqued/vintage, backlit LED' },
      { label: 'Thickness Options', value: '1/4" (6mm) standard, 3/8" (10mm) for large or heavy-use applications' },
      { label: 'Edge Finishes', value: 'Polished (clear edges), 1" beveled, 1.5" beveled, flat ground, pencil-polished' },
      { label: 'Safety Options', value: 'Vinyl safety backing, shatter-resistant film, tempered mirror for high-impact areas' },
      { label: 'Mounting Systems', value: 'J-bar channel, Z-clips, mirror mastic adhesive, French cleat for removable' },
      { label: 'Special Features', value: 'Anti-fog heating pads, integrated LED backlighting, touch-sensor dimming' },
    ],
    detailedContent: {
      mirrorTypes: [
        { name: 'Standard Silver Mirror', description: 'Traditional silver-backed float glass. Most economical. Not for high-moisture areas.' },
        { name: 'Copper-Free Mirror', description: 'Moisture-resistant backing for bathrooms. Prevents black-edge deterioration.' },
        { name: 'Antiqued/Vintage Mirror', description: 'Chemically treated for aged, distressed appearance. Decorative statement pieces.' },
        { name: 'Backlit LED Mirror', description: 'Integrated LED strip lighting behind frosted border. Modern vanity aesthetic.' },
      ],
      edgeOptions: [
        { name: 'Polished Edge', description: 'Clear, see-through edges. Clean modern look. Standard for frameless installation.' },
        { name: '1" Beveled Edge', description: 'Angled cut creates light-catching border. Classic elegance.' },
        { name: 'Flat Ground Edge', description: 'Smooth, non-sharp edges. Typically used when mirror will be framed.' },
      ],
    },
    ctaText: 'Get a Free Mirror Quote',
    ctaSubtext: 'Custom-cut to your exact specifications.',
  },
  'storefront-curtain-walls': {
    name: 'Storefront & Curtain Walls',
    shortName: 'Storefront Glass',
    parentPath: '/storefront-curtain-walls',
    heroImage: '/images/services/Impact-Storefront-and-IG-Portfolio.jpg',
    description: 'Commercial aluminum storefront and curtain wall systems engineered for performance. We install Kawneer, YKK AP, and Oldcastle systems with energy-efficient Low-E insulated glass units meeting ASHRAE 90.1 standards.',
    features: [
      'Kawneer, YKK AP, Oldcastle storefront systems',
      'Insulated glass units (IGUs) with Low-E coating',
      'Impact-rated glazing for coastal/high-wind zones',
      'Thermal aluminum framing (ASHRAE 90.1 compliant)',
      'Automatic door integration (Besam, Dorma, Assa Abloy)',
      'ADA-compliant entry configurations',
      'Curtain wall systems for multi-story applications',
      'Emergency/fire-rated glazing available',
    ],
    technicalSpecs: [
      { label: 'Frame Systems', value: 'Kawneer 451T, YKK AP YHS 50, Oldcastle 400 series aluminum storefront' },
      { label: 'Glazing Options', value: '1" insulated (IGU), laminated safety, tempered, Low-E coated, spandrel' },
      { label: 'Energy Performance', value: 'U-factor 0.29-0.45, SHGC 0.25-0.40, meets ASHRAE 90.1 and IECC codes' },
      { label: 'Impact Rating', value: 'Large/small missile impact-rated options for HVHZ (High Velocity Hurricane Zone)' },
      { label: 'Door Options', value: 'Manual swing, automatic sliding (Besam SL500), revolving, vestibule configurations' },
      { label: 'Fire Rating', value: '45-minute, 60-minute, and 90-minute fire-rated glazing assemblies available' },
    ],
    detailedContent: {
      frameSystems: [
        { name: 'Standard Storefront', description: 'Non-thermal aluminum framing. Economical for interior or mild climates.' },
        { name: 'Thermally Broken Storefront', description: 'Polyurethane thermal break prevents condensation. Required for energy codes.' },
        { name: 'Curtain Wall', description: 'Non-load-bearing facade system. Spans floor-to-floor. For multi-story buildings.' },
      ],
      glazingTypes: [
        { name: 'Insulated Glass Unit (IGU)', description: 'Two panes with air/argon gap. Standard for exterior applications.' },
        { name: 'Low-E Coated', description: 'Metallic coating reflects heat. Reduces cooling costs. Multiple performance levels.' },
        { name: 'Laminated Safety Glass', description: 'PVB interlayer holds glass if broken. Required for overhead and some storefronts.' },
        { name: 'Spandrel Glass', description: 'Opaque ceramic-coated glass. Hides floor plates and structure in curtain walls.' },
      ],
    },
    ctaText: 'Get a Free Commercial Glass Quote',
    ctaSubtext: 'Professional site assessment and engineering.',
  },
  'glass-railings': {
    name: 'Glass Railings',
    shortName: 'Glass Railings',
    parentPath: '/glass-railings',
    heroImage: '/images/portfolio/image-2.jpg',
    description: 'Architectural glass railing systems engineered for safety and unobstructed views. We install 1/2" tempered safety glass with 304 stainless steel (interior) or marine-grade 316 stainless steel (exterior/pool) hardware meeting IBC and IRC building codes.',
    features: [
      '1/2" (12mm) tempered safety glass panels',
      '304 stainless steel hardware (interior)',
      '316 marine-grade stainless (exterior/coastal)',
      'Base shoe, post-mount, standoff, and spigot systems',
      'IBC/IRC code-compliant 42" guardrail height',
      'Pool fence code compliant (no climbable rails)',
      'Clear, tinted gray, tinted bronze, frosted options',
      'Top rail options: stainless steel cap, wood, or frameless',
    ],
    technicalSpecs: [
      { label: 'Glass Specification', value: '1/2" (12mm) fully tempered safety glass, ASTM C1048 certified' },
      { label: 'Stainless Steel Grades', value: '304 SS for interior, 316 SS marine-grade for pools/coastal/exterior' },
      { label: 'Mounting Systems', value: 'Base shoe channel, round post with clamps, standoff pins, floor-mount spigots' },
      { label: 'Code Compliance', value: 'IBC/IRC 42" guardrail height, 4" sphere test, 200 lb point load tested' },
      { label: 'Top Rail Options', value: 'Frameless (no rail), round stainless cap rail, flat stainless, wood cap' },
      { label: 'Glass Options', value: 'Clear, low-iron ultra-clear, gray tint, bronze tint, acid-etched frosted' },
    ],
    detailedContent: {
      mountingSystems: [
        { name: 'Base Shoe Channel', description: 'Aluminum channel at floor level grips glass. Cleanest look. Popular for decks and balconies.' },
        { name: 'Post-Mount with Clamps', description: 'Round stainless posts with glass clamps. Classic appearance. Easy glass replacement.' },
        { name: 'Standoff Pins', description: 'Glass attached with decorative cylindrical standoffs. Modern minimalist aesthetic.' },
        { name: 'Spigot/Core-Mount', description: 'Glass mounted in floor-anchored spigot cores. Ultimate frameless look.' },
      ],
      steelGrades: [
        { name: '304 Stainless Steel', description: 'Standard grade. Corrosion-resistant. Ideal for interior applications.' },
        { name: '316 Stainless Steel', description: 'Marine-grade with molybdenum. Required for pools, coastal, and high-humidity environments.' },
        { name: 'Powder-Coated Options', description: 'Matte black, bronze, or custom colors available on steel components.' },
      ],
    },
    ctaText: 'Get a Free Glass Railing Quote',
    ctaSubtext: 'Code-compliant and professionally installed.',
  },
};

export const cities = {
  'new-braunfels': {
    name: 'New Braunfels',
    state: 'TX',
    county: 'Comal County',
    trustParagraph: 'At Comal Glass Company, we understand the unique character of New Braunfels \u2014 from the historic charm of Gruene to the luxury estates along the Guadalupe River. Whether you\'re finishing a custom build in Vintage Oaks or renovating a property near Landa Park, our glass solutions are precision-measured and installed to complement New Braunfels\' distinctive blend of Hill Country tradition and modern design.',
    landmarks: ['Gruene Historic District', 'Guadalupe River', 'Vintage Oaks', 'Landa Park'],
    nearbyAreas: 'San Marcos, Canyon Lake, and the greater Comal County area',
  },
  'boerne': {
    name: 'Boerne',
    state: 'TX',
    county: 'Kendall County',
    trustParagraph: 'At Comal Glass Company, we understand the architectural standards required for luxury estates in Boerne and the surrounding Kendall County. Whether you are finishing a modern build in Cordillera Ranch or a rustic-chic home near Sisterdale, our glass products are engineered to complement the unique Hill Country aesthetic. We work closely with local custom builders to ensure every installation meets the highest standards of the Boerne design community.',
    landmarks: ['Cordillera Ranch', 'Main Street Boerne', 'Cibolo Creek', 'Sisterdale'],
    nearbyAreas: 'Fair Oaks Ranch, Comfort, and the greater Kendall County area',
    // PRIORITY CITY - Enhanced unique content for SEO
    isPriority: true,
    localProjects: {
      headline: 'Why Boerne Homeowners Choose Comal Glass',
      content: 'Boerne\'s master-planned communities like Cordillera Ranch, Esperanza, and Fair Oaks Ranch feature homes where indoor-outdoor living is paramount. Our frameless shower enclosures and glass railings are designed to maximize Hill Country views while meeting the elevated HOA standards these communities require. We\'ve completed installations throughout the Reserve at Esperanza and George\'s Ranch, delivering the precision craftsmanship that Boerne homeowners expect.',
      featuredNeighborhoods: ['Cordillera Ranch', 'Reserve at Esperanza', 'Fair Oaks Ranch', 'George\'s Ranch'],
    },
    localFAQ: [
      {
        question: 'Do you work with Boerne HOAs on glass railing approvals?',
        answer: 'Yes. Communities like Cordillera Ranch and Fair Oaks Ranch have specific architectural guidelines. We provide detailed specifications and drawings to help expedite your HOA approval process, and our 316 marine-grade stainless steel hardware meets the durability requirements for Hill Country outdoor living.',
      },
      {
        question: 'How far is Comal Glass from Boerne?',
        answer: 'Our headquarters in Bulverde is just 20 minutes from downtown Boerne via Highway 46. We offer free in-home consultations throughout Kendall County, from Sisterdale to Comfort.',
      },
    ],
    drivingInfo: 'Just 20 minutes from downtown Boerne via Highway 46 — serving all of Kendall County from Sisterdale to Comfort.',
  },
  'bulverde': {
    name: 'Bulverde',
    state: 'TX',
    county: 'Comal County',
    trustParagraph: 'Located right here in Bulverde, Comal Glass Company is your truly local glass provider. We know the builders, the neighborhoods, and the design preferences of this fast-growing community. From new construction in Johnson Ranch to custom homes along the 281 corridor, we deliver the same precision and craftsmanship that has made us the Hill Country\'s trusted glass experts.',
    landmarks: ['Johnson Ranch', 'Bulverde Road corridor', 'Spring Branch ISD area'],
    nearbyAreas: 'Spring Branch, Canyon Lake, and northern San Antonio',
  },
  'blanco': {
    name: 'Blanco',
    state: 'TX',
    county: 'Blanco County',
    trustParagraph: 'Comal Glass Company proudly serves the Blanco community and surrounding Blanco County. From ranch properties along the Blanco River to charming homes near the historic town square, we bring precision glass craftsmanship to this beautiful corner of the Hill Country. Our team understands the rural elegance that defines Blanco — and we build to match it.',
    landmarks: ['Blanco River', 'Blanco State Park', 'Historic Town Square'],
    nearbyAreas: 'Johnson City, Wimberley, and the greater Blanco County area',
    // PRIORITY CITY - Enhanced unique content for SEO
    isPriority: true,
    localProjects: {
      headline: 'Why Blanco Property Owners Choose Comal Glass',
      content: 'Known as the "Lavender Capital of Texas," Blanco attracts homeowners who appreciate wide-open spaces, working ranches, and understated Hill Country elegance. Our glass installations complement the natural beauty of Blanco River properties and the charming residences surrounding the historic town square. Whether you\'re building a weekend retreat at Ranches at Brushy Top or renovating a home near Blanco State Park, we deliver craftsmanship that honors this community\'s character.',
      featuredNeighborhoods: ['Ranches at Brushy Top', 'Cielo Springs', 'Majestic Hills', 'Historic Blanco'],
    },
    localFAQ: [
      {
        question: 'Do you install wine room glass for Blanco area collectors?',
        answer: 'Absolutely. Blanco\'s proximity to the Texas Wine Trail makes wine storage a popular request. Our thermally-broken aluminum framing and dual-pane insulated glass maintain the optimal 55°F cellar temperature, protecting your collection from Texas heat.',
      },
      {
        question: 'How quickly can you serve Blanco from your location?',
        answer: 'Blanco is approximately 35 minutes from our Bulverde headquarters. We provide free consultations throughout Blanco County, including the surrounding ranches and rural properties.',
      },
    ],
    drivingInfo: 'Approximately 35 minutes from our Bulverde headquarters — serving the Lavender Capital and all of Blanco County.',
  },
  'canyon-lake': {
    name: 'Canyon Lake',
    state: 'TX',
    county: 'Comal County',
    trustParagraph: 'As longtime members of the Canyon Lake community, Comal Glass Company understands the unique demands of lakefront and Hill Country living. From vacation homes with panoramic lake views to permanent residences in Mystic Shores, we design and install glass solutions that maximize your views while standing up to the Texas elements. Our proximity to Canyon Lake means fast service and local expertise you can count on.',
    landmarks: ['Canyon Lake', 'Mystic Shores', 'Hancock Park', 'The Guadalupe River below the dam'],
    nearbyAreas: 'New Braunfels, Spring Branch, and Wimberley',
  },
  'spring-branch': {
    name: 'Spring Branch',
    state: 'TX',
    county: 'Comal County',
    trustParagraph: 'Comal Glass Company is proud to serve the Spring Branch community and the surrounding Hill Country corridor along Highway 281. From custom ranch homes to new developments, we bring expert glass craftsmanship to this growing area. Our familiarity with local builders and the architectural style of Spring Branch ensures every project is completed to the highest standard.',
    landmarks: ['Highway 281 corridor', 'Rebecca Creek', 'Spring Branch community'],
    nearbyAreas: 'Bulverde, Canyon Lake, and New Braunfels',
  },
  'fredericksburg': {
    name: 'Fredericksburg',
    state: 'TX',
    county: 'Gillespie County',
    trustParagraph: 'Comal Glass Company is honored to serve Fredericksburg and the wider Gillespie County area. Known for its German heritage, world-class wineries, and luxury ranch estates, Fredericksburg demands a level of craftsmanship that matches its reputation. Whether you\'re outfitting a boutique on Main Street or finishing a custom home in the wine country, our glass solutions are built to impress.',
    landmarks: ['Main Street Fredericksburg', 'Wine Road 290', 'Enchanted Rock area', 'Gillespie County ranches'],
    nearbyAreas: 'Stonewall, Luckenbach, and the Texas Wine Country',
    // PRIORITY CITY - Enhanced unique content for SEO
    isPriority: true,
    localProjects: {
      headline: 'Why Fredericksburg Homeowners Choose Comal Glass',
      content: 'Fredericksburg\'s luxury market — from Boot Ranch estates to vineyard properties along Wine Road 290 — demands glass work that matches the town\'s world-class reputation. We\'ve installed frameless shower enclosures and custom wine cellars in some of Gillespie County\'s most prestigious homes, including properties in Cool Water Ranch and Post Oak Lakes. Our precision craftsmanship complements the German heritage architecture and modern Hill Country aesthetics that define Fredericksburg living.',
      featuredNeighborhoods: ['Boot Ranch', 'Cool Water Ranch', 'Post Oak Lakes', 'Wine Road 290 corridor'],
    },
    localFAQ: [
      {
        question: 'Do you build custom wine cellars for Fredericksburg wine collectors?',
        answer: 'Yes — wine storage is one of our specialties, and Fredericksburg\'s wine culture makes it a natural fit. Our thermally-broken aluminum framing with dual-pane Low-E glass maintains optimal 55°F cellar conditions while showcasing your collection. We\'re compatible with WhisperKOOL, CellarPro, and Wine Guardian cooling systems.',
      },
      {
        question: 'How far is Comal Glass from Fredericksburg?',
        answer: 'Fredericksburg is approximately 50 minutes from our Bulverde headquarters via Highway 281 and 46. We serve all of Gillespie County, including Stonewall, Luckenbach, and the surrounding wine country estates.',
      },
    ],
    drivingInfo: 'Approximately 50 minutes from our Bulverde headquarters via Highway 281 — proudly serving Boot Ranch, Wine Road 290, and all of Gillespie County.',
  },
  'wimberley': {
    name: 'Wimberley',
    state: 'TX',
    county: 'Hays County',
    trustParagraph: 'Comal Glass Company is proud to serve the artistic community of Wimberley. Known for its creative spirit, stunning Cypress Creek, and the iconic Blue Hole, Wimberley attracts homeowners who appreciate unique craftsmanship and natural beauty. From eclectic artist studios to elegant Hill Country retreats near the Wimberley Square, we deliver glass solutions that honor this special town\'s character.',
    landmarks: ['Blue Hole Regional Park', 'Wimberley Square', 'Cypress Creek', 'Jacob\'s Well'],
    nearbyAreas: 'San Marcos, Dripping Springs, and the Hays County Hill Country',
  },
  'dripping-springs': {
    name: 'Dripping Springs',
    state: 'TX',
    county: 'Hays County',
    trustParagraph: 'Comal Glass Company proudly serves Dripping Springs — the Gateway to the Hill Country. This rapidly growing community is known for its thriving distilleries, boutique wineries, and sprawling ranch estates along Hamilton Pool Road. Whether you\'re building a modern farmhouse or upgrading a luxury property in one of the area\'s premier developments, our precision glass work matches the elevated standards of Dripping Springs living.',
    landmarks: ['Hamilton Pool Road', 'Mercer Street downtown', 'Dripping Springs wedding venues', 'Caliterra'],
    nearbyAreas: 'Austin, Wimberley, and Bee Cave',
    // PRIORITY CITY - Enhanced unique content for SEO
    isPriority: true,
    localProjects: {
      headline: 'Why Dripping Springs Homeowners Choose Comal Glass',
      content: 'As the "Gateway to the Hill Country," Dripping Springs has exploded with luxury home construction — and discerning homeowners here expect perfection. We\'ve installed frameless shower enclosures and glass railings in Caliterra, Caliza Reserve, and along the scenic Hamilton Pool Road corridor. The modern farmhouse aesthetic popular in Dripping Springs pairs beautifully with our matte black hardware finishes and low-iron ultra-clear Starphire® glass.',
      featuredNeighborhoods: ['Caliterra', 'Caliza Reserve', 'Headwaters', 'Hamilton Pool Road estates'],
    },
    localFAQ: [
      {
        question: 'What hardware finishes are popular in Dripping Springs homes?',
        answer: 'The modern farmhouse and contemporary designs in Dripping Springs often feature matte black or brushed gold (satin brass) hardware. These finishes complement the clean lines and natural materials popular in Hill Country modern architecture.',
      },
      {
        question: 'Do you serve the Hamilton Pool Road area?',
        answer: 'Yes. We provide free in-home consultations throughout Dripping Springs, including the ranch estates along Hamilton Pool Road, Fitzhugh Road, and all surrounding Hays County communities.',
      },
    ],
    drivingInfo: 'Approximately 45 minutes from our Bulverde headquarters — serving Caliterra, Hamilton Pool Road, and all of Hays County.',
  },
  'driftwood': {
    name: 'Driftwood',
    state: 'TX',
    county: 'Hays County',
    trustParagraph: 'Comal Glass Company is honored to serve the Driftwood community, where Texas barbecue traditions meet Hill Country sophistication. Home to legendary Salt Lick BBQ and surrounded by rolling vineyards, Driftwood attracts those who appreciate the finer things in life. Our glass installations complement the rustic elegance and outdoor living that define this unique corner of Texas.',
    landmarks: ['Salt Lick BBQ', 'Driftwood Estate Winery', 'Vista Brewing', 'Camp Ben McCulloch'],
    nearbyAreas: 'Dripping Springs, Wimberley, and Buda',
  },
  'johnson-city': {
    name: 'Johnson City',
    state: 'TX',
    county: 'Blanco County',
    trustParagraph: 'Comal Glass Company proudly serves Johnson City, the heart of the Texas Wine Trail and birthplace of President Lyndon B. Johnson. This charming Hill Country town blends rich history with a growing wine and hospitality scene. From historic homes near the LBJ National Historical Park to new builds in the surrounding ranch lands, we bring expert glass craftsmanship to Johnson City\'s unique blend of heritage and Hill Country living.',
    landmarks: ['LBJ National Historical Park', 'Texas Wine Trail', 'Pedernales River', 'Johnson City town square'],
    nearbyAreas: 'Fredericksburg, Marble Falls, and Stonewall',
    // PRIORITY CITY - Enhanced unique content for SEO
    isPriority: true,
    localProjects: {
      headline: 'Why Johnson City Homeowners Choose Comal Glass',
      content: 'Johnson City sits at the crossroads of the Texas Wine Trail and LBJ heritage — attracting homeowners who value both history and Hill Country elegance. Our glass installations are found in custom homes along the Pedernales River, weekend retreats near the town square, and wine country estates throughout Blanco County. We understand the balance of rustic charm and modern craftsmanship that Johnson City demands.',
      featuredNeighborhoods: ['Pedernales River properties', 'Texas Wine Trail corridor', 'Johnson City town square area'],
    },
    localFAQ: [
      {
        question: 'Can you install glass wine rooms for Texas Wine Trail enthusiasts?',
        answer: 'Absolutely. Johnson City\'s location at the heart of the Texas Wine Trail makes proper wine storage essential. Our climate-sealed glass wine enclosures with Low-E coating protect your collection from UV damage while maintaining optimal 55°F storage temperature.',
      },
      {
        question: 'How far is Johnson City from Comal Glass?',
        answer: 'Johnson City is approximately 45 minutes from our Bulverde headquarters via Highway 281. We serve all of Blanco County, including properties along the Pedernales River and the Texas Wine Trail.',
      },
    ],
    drivingInfo: 'Approximately 45 minutes from our Bulverde headquarters via Highway 281 — serving the Texas Wine Trail and all of Blanco County.',
  },
  'stonewall': {
    name: 'Stonewall',
    state: 'TX',
    county: 'Gillespie County',
    trustParagraph: 'Comal Glass Company is proud to serve Stonewall, nestled along the scenic Highway 290 wine corridor in the heart of Texas Wine Country. Famous for its peach orchards, lavender fields, and proximity to the LBJ Ranch, Stonewall embodies Hill Country charm at its finest. Our glass solutions complement the vineyard estates, weekend retreats, and custom homes that make this area truly special.',
    landmarks: ['LBJ Ranch', 'Highway 290 Wine Trail', 'Stonewall peach orchards', 'Grape Creek Vineyards'],
    nearbyAreas: 'Fredericksburg, Johnson City, and Luckenbach',
  },
  'kendalia': {
    name: 'Kendalia',
    state: 'TX',
    county: 'Kendall County',
    trustParagraph: 'Comal Glass Company proudly serves the quiet, rural community of Kendalia in Kendall County. This peaceful Hill Country enclave is known for its wide-open spaces, working ranches, and residents who value privacy and quality craftsmanship. Whether you\'re building a custom ranch home or upgrading an existing property along the winding country roads, our glass work meets the high standards of Kendalia living.',
    landmarks: ['Kendalia country roads', 'Guadalupe River access', 'Rural Kendall County ranches'],
    nearbyAreas: 'Boerne, Blanco, and Sisterdale',
  },
  'marble-falls': {
    name: 'Marble Falls',
    state: 'TX',
    county: 'Burnet County',
    trustParagraph: 'Comal Glass Company is honored to serve Marble Falls, the sparkling gem of the Highland Lakes region. Situated along the beautiful Colorado River and Lake Marble Falls, this vibrant community attracts those seeking waterfront living and Hill Country adventure. From lakeside homes with panoramic views to Main Street businesses in the historic downtown, our glass installations capture the beauty of Marble Falls living.',
    landmarks: ['Lake Marble Falls', 'Main Street downtown', 'Lakeside Park', 'Falls on the Colorado'],
    nearbyAreas: 'Horseshoe Bay, Burnet, and Kingsland',
    // PRIORITY CITY - Enhanced unique content for SEO
    isPriority: true,
    localProjects: {
      headline: 'Why Marble Falls Homeowners Choose Comal Glass',
      content: 'Marble Falls\' stunning Highland Lakes setting attracts homeowners who want to maximize their waterfront views. Our frameless glass railings and floor-to-ceiling shower enclosures are engineered for lakeside living — using 316 marine-grade stainless steel hardware that withstands the humidity and elements. We\'ve completed installations in homes overlooking Lake Marble Falls and throughout the Horseshoe Bay area, bringing precision craftsmanship to the unique demands of lakefront construction.',
      featuredNeighborhoods: ['Lake Marble Falls waterfront', 'Horseshoe Bay', 'Highland Lakes area', 'Main Street historic district'],
    },
    localFAQ: [
      {
        question: 'Do you use marine-grade hardware for Marble Falls lakefront homes?',
        answer: 'Yes. All our exterior glass railing installations near water use 316 marine-grade stainless steel hardware, which contains molybdenum for superior corrosion resistance. This is essential for Highland Lakes properties exposed to humidity and moisture.',
      },
      {
        question: 'How far is Marble Falls from Comal Glass?',
        answer: 'Marble Falls is approximately 55 minutes from our Bulverde headquarters. We serve all of Burnet County, including Horseshoe Bay, Kingsland, and the entire Highland Lakes region.',
      },
    ],
    drivingInfo: 'Approximately 55 minutes from our Bulverde headquarters — serving the Highland Lakes, Horseshoe Bay, and all of Burnet County.',
  },
  'lakeway': {
    name: 'Lakeway',
    state: 'TX',
    county: 'Travis County',
    trustParagraph: 'Comal Glass Company proudly serves Lakeway, one of the premier lakeside communities in the Austin metro area. Known for its luxury homes along Lake Travis, world-class golf courses, and resort-style living, Lakeway residents expect nothing but the best. Our precision glass installations — from expansive shower enclosures to stunning wine rooms — meet the elevated standards of this sophisticated Hill Country community.',
    landmarks: ['Lake Travis', 'Lakeway Resort and Spa', 'The Hills of Lakeway', 'Rough Hollow'],
    nearbyAreas: 'Bee Cave, Austin, and Spicewood',
  },
  'spicewood': {
    name: 'Spicewood',
    state: 'TX',
    county: 'Burnet County',
    trustParagraph: 'Comal Glass Company is proud to serve Spicewood, where Hill Country charm meets Lake Travis waterfront living. This growing community west of Austin is known for its scenic beauty, legendary music venues, and luxury lake estates. Whether you\'re building a weekend retreat near Krause Springs or a permanent residence overlooking the lake, our expert glass craftsmanship brings your vision to life.',
    landmarks: ['Lake Travis', 'Krause Springs', 'Spicewood Vineyards', 'Pace Bend Park'],
    nearbyAreas: 'Marble Falls, Bee Cave, and Lakeway',
  },
};

export const builderBenefits = [
  {
    title: 'Digital Laser Templating',
    description: 'Perfect fit for out-of-plumb walls and non-standard openings.',
  },
  {
    title: 'On-Time Performance',
    description: 'We respect your closing dates and project timelines.',
  },
  {
    title: 'Clean Job Sites',
    description: 'White-glove installation that protects your finishes.',
  },
  {
    title: 'Direct Communication',
    description: 'One point of contact from measure to install \u2014 no runaround.',
  },
];

// Generate all route combinations
export const getLocalRoutes = () => {
  const routes = [];
  Object.keys(services).forEach(serviceSlug => {
    Object.keys(cities).forEach(citySlug => {
      routes.push({
        path: `/${serviceSlug}-${citySlug}`,
        serviceSlug,
        citySlug,
      });
    });
  });
  return routes;
};

// Get city pages for a specific service (for "Proudly Serving" sections)
export const getCityPagesForService = (serviceSlug) => {
  return Object.entries(cities).map(([citySlug, city]) => ({
    path: `/${serviceSlug}-${citySlug}`,
    cityName: city.name,
    citySlug,
  }));
};

// Get service pages for a specific city (for cross-linking)
export const getServicePagesForCity = (citySlug) => {
  return Object.entries(services).map(([serviceSlug, service]) => ({
    path: `/${serviceSlug}-${citySlug}`,
    serviceName: service.name,
    serviceSlug,
  }));
};

// Nearby cities mapping for internal cross-linking (geographic proximity)
export const nearbyCities = {
  'new-braunfels': ['canyon-lake', 'bulverde', 'spring-branch', 'wimberley'],
  'boerne': ['bulverde', 'spring-branch', 'kendalia', 'blanco'],
  'bulverde': ['spring-branch', 'canyon-lake', 'new-braunfels', 'boerne'],
  'blanco': ['johnson-city', 'wimberley', 'dripping-springs', 'kendalia'],
  'canyon-lake': ['new-braunfels', 'bulverde', 'spring-branch', 'wimberley'],
  'spring-branch': ['bulverde', 'canyon-lake', 'boerne', 'kendalia'],
  'fredericksburg': ['stonewall', 'johnson-city', 'blanco', 'marble-falls'],
  'wimberley': ['dripping-springs', 'blanco', 'canyon-lake', 'driftwood'],
  'dripping-springs': ['wimberley', 'driftwood', 'lakeway', 'blanco'],
  'driftwood': ['dripping-springs', 'wimberley', 'blanco'],
  'johnson-city': ['fredericksburg', 'stonewall', 'blanco', 'marble-falls'],
  'stonewall': ['fredericksburg', 'johnson-city', 'blanco'],
  'kendalia': ['boerne', 'blanco', 'spring-branch'],
  'marble-falls': ['spicewood', 'lakeway', 'johnson-city', 'fredericksburg'],
  'lakeway': ['spicewood', 'dripping-springs', 'marble-falls'],
  'spicewood': ['marble-falls', 'lakeway', 'dripping-springs'],
};

// Get nearby city pages for a specific city and service (for cross-linking)
export const getNearbyCityPages = (currentCitySlug, serviceSlug) => {
  const nearby = nearbyCities[currentCitySlug] || [];
  return nearby
    .filter(citySlug => cities[citySlug]) // Ensure city exists
    .map(citySlug => ({
      path: `/${serviceSlug}-${citySlug}`,
      cityName: cities[citySlug].name,
      citySlug,
    }));
};


// Local project photos for Featured Projects section
// Now supports multiple projects per area with case-study style descriptions
export const localProjects = {
  // Boerne - 3 projects
  'shower-glass-boerne': [
    {
      image: '/images/local-projects/shower-boerne.jpg',
      title: 'Hill Country Rustic Primary',
      description: 'Frameless enclosure with brushed nickel hardware and exposed wood beam ceiling detail.',
      caseStudy: 'The homeowner wanted glass that would complement the rustic wood beams without competing with them. Comal Glass delivered a minimal frameless design with low-profile brushed nickel hardware that lets the architecture shine.',
    },
    {
      image: '/images/local-projects/shower-boerne-2.jpg',
      title: 'Contemporary Marble Spa',
      description: 'Floor-to-ceiling frameless enclosure with matte black hardware and Calacatta marble.',
      caseStudy: 'This Cordillera Ranch homeowner requested a spa-like experience with clean lines. Comal Glass installed a corner frameless enclosure with hexagon ceiling tile detail and a rain showerhead cutout.',
    },
    {
      image: '/images/local-projects/shower-boerne-steam.jpg',
      title: 'Wood-Look Tile Steam Enclosure',
      description: 'Custom steam shower with vertical wood-look tile surround and matte black hardware.',
      caseStudy: 'The client wanted a Japanese-inspired steam shower experience with a warm, natural aesthetic. Comal Glass installed a floor-to-ceiling frameless enclosure that showcases the popular vertical wood-look tile and pebble floor.',
    },
  ],
  // Blanco - 2 projects
  'shower-glass-blanco': [
    {
      image: '/images/local-projects/shower-blanco.jpg',
      title: 'Luxury Wet Room',
      description: 'Open-concept frameless glass with satin brass hardware and freestanding tub.',
      caseStudy: 'This Blanco River property owner envisioned a seamless wet room design. Comal Glass delivered by installing a single fixed panel with satin brass hardware that maintains the open, airy feel while containing water.',
    },
    {
      image: '/images/local-projects/shower-blanco-2.jpg',
      title: 'Minimalist Guest Bath',
      description: 'Clean single-panel splash guard with brushed nickel clip for compact bathroom.',
      caseStudy: 'The homeowner needed a simple, elegant solution for their guest bathroom. Comal Glass recommended a minimalist fixed panel that maximizes the small space while providing effective water containment.',
    },
  ],
  // Fredericksburg - Wine Enclosure (updated from shower)
  'wine-enclosures-fredericksburg': [
    {
      image: '/images/local-projects/wine-room-fredericksburg.jpg',
      title: 'Modern Wine Country Cellar',
      description: 'Floor-to-ceiling frameless wine enclosure with clean lines and minimal hardware.',
      caseStudy: 'This Boot Ranch homeowner wanted a wine room that felt contemporary yet timeless. Comal Glass delivered with ultra-clear low-iron glass and minimal visible hardware, letting the collection and architecture speak for themselves.',
    },
  ],
  'shower-glass-fredericksburg': [
    {
      image: '/images/local-projects/shower-fredericksburg-bronze.jpg',
      title: 'Old-World Bronze Enclosure',
      description: 'Full bronze grid shower enclosure with divided-lite panels and brushed gold fixtures.',
      caseStudy: 'This Fredericksburg homeowner wanted a European-inspired bathroom with old-world charm. Comal Glass installed a full bronze enclosure that will naturally patina over time, complementing the brushed gold fixtures and creating that timeless wine country aesthetic.',
    },
  ],
  // Marble Falls
  'wine-enclosures-marble-falls': [
    {
      image: '/images/local-projects/wine-room-marble-falls.jpg',
      title: 'Highland Lakes Installation',
      description: 'On-site at a lakefront property overlooking Lake Marble Falls.',
      caseStudy: 'This Highland Lakes homeowner wanted a climate-controlled wine room with lake views. Comal Glass delivered a custom thermally-broken aluminum frame system to maintain optimal cellar temperature.',
    },
  ],
  // Dripping Springs
  'shower-glass-dripping-springs': [
    {
      image: '/images/local-projects/shower-dripping-springs.jpg',
      title: 'Modern Farmhouse Primary',
      description: 'Spacious frameless enclosure with satin brass hardware and herringbone tile.',
      caseStudy: 'The Caliterra homeowners wanted their shower to feel like a continuation of the bathroom. Comal Glass installed a frameless enclosure with warm satin brass hardware to complement the herringbone pattern.',
    },
  ],
  // Johnson City
  'shower-glass-johnson-city': [
    {
      image: '/images/local-projects/shower-johnson-city.jpg',
      title: 'Wine Country Retreat',
      description: 'Arched ceiling frameless enclosure with brushed nickel hardware and built-in niche.',
      caseStudy: 'This Johnson City weekend home needed glass that would work with a unique arched ceiling. Comal Glass custom-cut panels to follow the curve and finished with classic brushed nickel hardware.',
    },
  ],
  // Canyon Lake - 3 projects
  'shower-glass-canyon-lake': [
    {
      image: '/images/local-projects/shower-canyon-lake.jpg',
      title: 'Lakefront Primary Suite',
      description: 'Full frameless enclosure with matte black hardware and large-format tile.',
      caseStudy: 'The homeowner wanted a hotel-quality shower experience in their lakefront home. Comal Glass delivered a spacious corner enclosure with clean matte black hardware.',
    },
    {
      image: '/images/local-projects/shower-canyon-lake-2.jpg',
      title: 'Corner Neo-Angle Design',
      description: 'Five-panel neo-angle enclosure with matte black hardware alongside soaking tub.',
      caseStudy: 'This client had an existing corner soaking tub they wanted to complement. Comal Glass designed a custom neo-angle enclosure that maximizes space while creating visual symmetry with the tub.',
    },
  ],
  'wine-enclosures-canyon-lake': [
    {
      image: '/images/local-projects/wine-room-canyon-lake.jpg',
      title: 'Contemporary Wine Display',
      description: 'Dual frameless glass wine walls with integrated metal wine racks.',
      caseStudy: 'The client wanted their wine collection to be a focal point, not hidden away. Comal Glass installed floor-to-ceiling frameless panels that showcase the collection from both the living and dining areas.',
    },
  ],
  // Spring Branch
  'shower-glass-spring-branch': [
    {
      image: '/images/local-projects/shower-spring-branch.jpg',
      title: 'Hill Country Views',
      description: 'Corner frameless shower with satin brass hardware overlooking the countryside.',
      caseStudy: 'This Spring Branch property features stunning Hill Country views the owners wanted to enjoy from every room. Comal Glass installed a corner enclosure positioned to frame the window perfectly.',
    },
  ],
  // Bulverde
  'wine-enclosures-bulverde': [
    {
      image: '/images/local-projects/wine-room-bulverde.jpg',
      title: 'Custom Wine Room',
      description: 'Floor-to-ceiling frameless wine enclosure with satin brass ladder pull.',
      caseStudy: 'The homeowner wanted a wine room that felt like a jewel box. Comal Glass delivered with a single frameless door featuring an elegant satin brass ladder pull, creating a gallery-like entrance to their collection.',
    },
  ],
  // New Braunfels
  'shower-glass-new-braunfels': [
    {
      image: '/images/local-projects/shower-new-braunfels.jpg',
      title: 'Bold Green Statement',
      description: 'Frameless enclosure with brushed gold hardware against emerald green kit-kat tile.',
      caseStudy: 'This New Braunfels homeowner wasn\'t afraid of color. Comal Glass matched the bold design with warm brushed gold hardware and ultra-clear glass to let the striking green tile take center stage.',
    },
  ],
  // Canyon Lake - Office Partition
  'office-partitions-canyon-lake': [
    {
      image: '/images/local-projects/office-partition-canyon-lake.jpg',
      title: 'Modern Home Office Partition',
      description: 'Black grid divided-lite sliding doors creating a dedicated workspace.',
      caseStudy: 'The homeowner needed to carve out a home office from their open floor plan without sacrificing natural light. Comal Glass delivered industrial-style sliding glass doors with matte black grid framing that provide privacy while maintaining the home\'s modern aesthetic.',
    },
  ],
};

// Get project photos for a specific local page (returns array for multiple projects)
export const getLocalProjects = (serviceSlug, citySlug) => {
  const key = `${serviceSlug}-${citySlug}`;
  return localProjects[key] || null;
};

// Legacy function for backward compatibility - returns first project
export const getLocalProject = (serviceSlug, citySlug) => {
  const projects = getLocalProjects(serviceSlug, citySlug);
  return projects && projects.length > 0 ? projects[0] : null;
};

